import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { v4 as uuidv4 } from 'uuid';
import { Send, X, Bot, ChevronUp, ChevronDown } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../../lib/apiClient';
import { useLocation } from 'react-router-dom';

type ChatMessage = {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: number;
};

// En /messages arranca cerrado: abierto tapaba la lista, el chat, el aviso "Nuevo mensaje" y el compositor.
export function isMessagesPath(pathname: string): boolean {
  return pathname === '/messages' || pathname.startsWith('/messages/');
}

export function AIChatWidget() {
  const { isAuthenticated, user } = useAuth();
  const { currentTheme } = useTheme();
  const { t, i18n } = useTranslation();
  // En /condo arranca cerrado: abierto tapaba acciones del wizard de import a 1280x720 (BUG-HOA-05).
  const { pathname } = useLocation();
  const [isOpen, setIsOpen] = useState(
    () => !pathname.startsWith('/condo') && !isMessagesPath(pathname)
  );
  // No hay preferencia persistida (ni localStorage ni sessionStorage). Solo se respeta, en memoria,
  // que el usuario lo haya abierto a mano: así no se cierra solo al entrar a /messages.
  const userOpenedRef = useRef(false);
  const openChat = () => { userOpenedRef.current = true; setIsOpen(true); };
  const closeChat = () => { userOpenedRef.current = false; setIsOpen(false); };
  useEffect(() => {
    // El widget vive fuera de <Routes> y no se remonta al navegar: al entrar a /messages lo cerramos
    // salvo que el usuario lo haya abierto a mano. En las demás rutas no se toca nada.
    if (isMessagesPath(pathname) && !userOpenedRef.current) setIsOpen(false);
  }, [pathname]);
  const [isMinimized, setIsMinimized] = useState(false);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);

  const tenantBusinessType: 'vacation_rental' | 'long_term_rental' | 'hybrid' | undefined =
    (user as any)?.tenant_business_type;

  const userRole: string | undefined = (user as any)?.role;

  const uiLang: 'es' | 'en' =
    i18n?.language && i18n.language.startsWith('es') ? 'es' : 'en';

  const [messages, setMessages] = useState<ChatMessage[]>(() => {
    const role = String(userRole || '').toLowerCase();
    const isRenter = role === 'renter';
    const isPO = role === 'property_owner';
    const isEmployee = role === 'employee';
    const bt = String(tenantBusinessType || '').toLowerCase() as
      | 'vacation_rental'
      | 'long_term_rental'
      | 'hybrid'
      | '';
    const isLongTerm = bt === 'long_term_rental' || bt === 'hybrid';

    let welcome: string;

    if (isRenter) {
      if (isLongTerm) {
        welcome =
          uiLang === 'es'
            ? '¡Hola! Soy **SyncLyst AI**, tu asistente personal como Inquilino. ' +
              'Puedo ayudarte en español o inglés con:\n' +
              '- Ayuda con mi Contrato y Pagos (renta, saldo, vencimientos)\n' +
              '- Ayuda a subir comprobantes / fotos de transferencia\n' +
              '- Ayuda con Tickets de Servicio / Mantenimiento\n' +
              '- Ayuda con mis Mensajes / Threads (agencia o dueño)\n' +
              '- Ayuda con Avisos y noticias de la agencia'
            : 'Hi! I\'m **SyncLyst AI**, your personal assistant as a Renter. ' +
              'I can help you in English or Spanish with:\n' +
              '- Help with my Contract & Payments (rent, balance, due dates)\n' +
              '- Help uploading receipts / bank transfer photos\n' +
              '- Help with Service / Maintenance Tickets\n' +
              '- Help with my Messages / Threads (agency or landlord)\n' +
              '- Help with Announcements & agency news';
      } else {
        welcome =
          uiLang === 'es'
            ? '¡Hola! Soy **SyncLyst AI**, tu asistente personal como Huésped / Inquilino. ' +
              'Puedo ayudarte en español o inglés con:\n' +
              '- Ayuda con Tickets de Servicio / Mantenimiento\n' +
              '- Ayuda con mis Mensajes / Threads con la agencia\n' +
              '- Ayuda con Avisos y noticias\n' +
              '- Ayuda con los Welcome Books y guías de la propiedad'
            : 'Hi! I\'m **SyncLyst AI**, your personal assistant as a Guest / Renter. ' +
              'I can help you in English or Spanish with:\n' +
              '- Help with Service / Maintenance Tickets\n' +
              '- Help with my Messages / Threads with the agency\n' +
              '- Help with Announcements & news\n' +
              '- Help with Welcome Books & property guides';
      }
    } else if (isPO) {
      if (isLongTerm) {
        welcome =
          uiLang === 'es'
            ? '¡Hola! Soy **SyncLyst AI**, tu asistente personal como Propietario. ' +
              'Puedo ayudarte en español o inglés con:\n' +
              '- Ayuda con Mis Propiedades y vacantes\n' +
              '- Ayuda con Contratos, inquilinos y cobros (rentas)\n' +
              '- Ayuda con Tickets de Servicio reportados\n' +
              '- Ayuda con Avisos y noticias de la agencia'
            : 'Hi! I\'m **SyncLyst AI**, your personal assistant as a Property Owner. ' +
              'I can help you in English or Spanish with:\n' +
              '- Help with My Properties & vacancies\n' +
              '- Help with Contracts, tenants & rent collections\n' +
              '- Help with reported Service Tickets\n' +
              '- Help with Announcements & agency news';
      } else {
        welcome =
          uiLang === 'es'
            ? '¡Hola! Soy **SyncLyst AI**, tu asistente personal como Propietario. ' +
              'Puedo ayudarte en español o inglés con:\n' +
              '- Ayuda con Mis Propiedades y estado de ocupación\n' +
              '- Ayuda con Tickets de Servicio de mis casas\n' +
              '- Ayuda con Avisos y noticias de la agencia'
            : 'Hi! I\'m **SyncLyst AI**, your personal assistant as a Property Owner. ' +
              'I can help you in English or Spanish with:\n' +
              '- Help with My Properties & occupancy status\n' +
              '- Help with Service Tickets from my houses\n' +
              '- Help with Announcements & agency news';
      }
    } else if (isEmployee) {
      if (isLongTerm) {
        welcome =
          uiLang === 'es'
            ? '¡Hola! Soy **SyncLyst AI**. Como **Empleado**, puedo ayudarte en español o inglés con tus módulos del menú:\n' +
              '- Ayuda con Anuncios y noticias de la agencia\n' +
              '- Ayuda con mi Horario de trabajo (My Schedule)\n' +
              '- Ayuda con mis Tareas y plantillas (My Tasks / Task Templates)\n' +
              '- Ayuda con mi Sincronización de Calendario (Calendar Sync)\n' +
              '- Ayuda con Tickets de Servicio\n' +
              '- Ayuda con Inventario\n' +
              '- Ayuda con Mis Entrenamientos y Formación (My Trainings)\n' +
              '- Ayuda con Mis Mensajes / Threads con el equipo\n\n' +
              'Para propiedades, usuarios, roles, reportes globales o contratos de inquilinos, contacta a tu Manager 🙌'
            : 'Hi! I\'m **SyncLyst AI**. As an **Employee**, I can help you in English or Spanish with the modules from your menu:\n' +
              '- Help with Announcements & agency news\n' +
              '- Help with My Schedule (working hours & availability)\n' +
              '- Help with My Tasks & Task Templates\n' +
              '- Help with Calendar Sync (external calendar integration)\n' +
              '- Help with Service Tickets\n' +
              '- Help with Inventory\n' +
              '- Help with My Trainings & learning content\n' +
              '- Help with My Messages / Threads with the team\n\n' +
              'For Properties, Users & roles, global Reports, or Leases & tenants, contact your Manager 🙌';
      } else {
        welcome =
          uiLang === 'es'
            ? '¡Hola! Soy **SyncLyst AI**. Como **Empleado**, puedo ayudarte en español o inglés con tus módulos del menú:\n' +
              '- Ayuda con Anuncios y noticias de la agencia\n' +
              '- Ayuda con mi Horario de trabajo (My Schedule)\n' +
              '- Ayuda con mis Tareas y plantillas (My Tasks / Task Templates)\n' +
              '- Ayuda con mi Sincronización de Calendario (Calendar Sync)\n' +
              '- Ayuda con Tickets de Servicio\n' +
              '- Ayuda con Inventario\n' +
              '- Ayuda con Mis Entrenamientos y Formación (My Trainings)\n' +
              '- Ayuda con Mis Mensajes / Threads con el equipo\n\n' +
              'Para Propiedades, Usuarios, Reportes globales o Reservas de clientes, contacta a tu Manager 🙌'
            : 'Hi! I\'m **SyncLyst AI**. As an **Employee**, I can help you in English or Spanish with the modules from your menu:\n' +
              '- Help with Announcements & agency news\n' +
              '- Help with My Schedule (working hours & availability)\n' +
              '- Help with My Tasks & Task Templates\n' +
              '- Help with Calendar Sync (external calendar integration)\n' +
              '- Help with Service Tickets\n' +
              '- Help with Inventory\n' +
              '- Help with My Trainings & learning content\n' +
              '- Help with My Messages / Threads with the team\n\n' +
              'For Properties, Users & roles, global Reports, or Guest Bookings, contact your Manager 🙌';
      }
    } else {
      if (isLongTerm) {
        welcome =
          uiLang === 'es'
            ? '¡Hola! Soy **SyncLyst AI**. Puedo ayudarte en español o inglés con cualquier módulo. Por ejemplo:\n' +
              '- Ayuda con Calendario\n' +
              '- Ayuda con Mapa\n' +
              '- Ayuda con Tareas y Plantillas de tareas\n' +
              '- Ayuda con Tickets de Servicio\n' +
              '- Ayuda con Inventario\n' +
              '- Ayuda con Anuncios\n' +
              '- Ayuda con Propiedades\n' +
              '- Ayuda con Usuarios, empleados y roles\n' +
              '- Ayuda con Reportes (incluyendo rentas e inquilinos)\n' +
              '- Ayuda con Contratos e inquilinos (Larga Temporada)'
            : 'Hi! I\'m **SyncLyst AI**. I can help you in English or Spanish with any module. For example:\n' +
              '- Help with Calendar\n' +
              '- Help with Map\n' +
              '- Help with Tasks & Task Templates\n' +
              '- Help with Service Tickets\n' +
              '- Help with Inventory\n' +
              '- Help with Announcements\n' +
              '- Help with Properties\n' +
              '- Help with Users, employees & roles\n' +
              '- Help with Reports (including rents & tenants)\n' +
              '- Help with Contracts & tenants (Long-Term)';
      } else {
        welcome =
          uiLang === 'es'
            ? '¡Hola! Soy **SyncLyst AI**. Puedo ayudarte en español o inglés con cualquier módulo. Por ejemplo:\n' +
              '- Ayuda con Calendario\n' +
              '- Ayuda con Mapa\n' +
              '- Ayuda con Reservas (Bookings)\n' +
              '- Ayuda con Tareas y Plantillas de tareas\n' +
              '- Ayuda con Tickets de Servicio\n' +
              '- Ayuda con Inventario\n' +
              '- Ayuda con Anuncios\n' +
              '- Ayuda con Propiedades\n' +
              '- Ayuda con Usuarios, empleados y roles\n' +
              '- Ayuda con Reportes de cortas'
            : 'Hi! I\'m **SyncLyst AI**. I can help you in English or Spanish with any module. For example:\n' +
              '- Help with Calendar\n' +
              '- Help with Map\n' +
              '- Help with Bookings\n' +
              '- Help with Tasks & Task Templates\n' +
              '- Help with Service Tickets\n' +
              '- Help with Inventory\n' +
              '- Help with Announcements\n' +
              '- Help with Properties\n' +
              '- Help with Users, employees & roles\n' +
              '- Help with Short-Term Reports';
      }
    }

    return [
      {
        id: uuidv4(),
        role: 'assistant',
        content: welcome,
        timestamp: Date.now(),
      },
    ];
  });
  const conversationIdRef = useRef<string>(uuidv4());
  const listRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (isOpen && !isMinimized && listRef.current) {
      const el = listRef.current;
      requestAnimationFrame(() => {
        el.scrollTop = el.scrollHeight;
      });
    }
  }, [isOpen, isMinimized]);

  const sendToAssistant = useCallback(
    async (payload: { message: string; conversation_id: string; lang: string }) => {
      setLoading(true);
      try {
        const { data } = await apiClient.post('/api/assistant/chat', payload);

        const replyText =
          data?.reply ||
          data?.message ||
          data?.result ||
          data?.text ||
          data?.output ||
          'Procesé tu solicitud, pero no recibí una respuesta legible.';

        setMessages((prev) => [
          ...prev,
          {
            id: uuidv4(),
            role: 'assistant',
            content: String(replyText),
            timestamp: Date.now(),
          },
        ]);
      } catch (err) {
        const msg =
          err instanceof Error
            ? `Error del asistente: ${err.message}`
            : 'Error del asistente';
        setMessages((prev) => [
          ...prev,
          {
            id: uuidv4(),
            role: 'assistant',
            content: msg,
            timestamp: Date.now(),
          },
        ]);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const handleSend = async (customMessage?: string) => {
    const trimmed = (customMessage ?? input).trim();
    if (!trimmed) return;
    setInput('');
    setMessages((prev) => [
      ...prev,
      { id: uuidv4(), role: 'user', content: trimmed, timestamp: Date.now() },
    ]);

    const lang =
      (i18n?.language && i18n.language.startsWith('es')) ? 'es' : 'en';

    const payload = {
      message: trimmed,
      conversation_id: conversationIdRef.current,
      lang,
    };

    await sendToAssistant(payload);
  };

  const assistantName = t('ai_assistant_name', 'SyncLyst AI');
  const isDarkTheme = currentTheme === 'dark';
  const launcherClassName = isDarkTheme
    ? 'fixed bottom-6 right-6 z-[9999] flex items-center gap-2 rounded-full border border-slate-700/70 bg-slate-800/90 px-3 py-3 text-slate-100 shadow-[0_8px_24px_rgba(15,23,42,0.45)] backdrop-blur-sm transition hover:border-slate-600 hover:bg-slate-800'
    : 'fixed bottom-6 right-6 z-[9999] flex items-center gap-2 rounded-full border border-blue-100 bg-white px-3 py-3 text-slate-700 shadow-lg transition hover:border-blue-200 hover:bg-blue-50';
  const shellClassName = isDarkTheme
    ? 'overflow-hidden rounded-xl bg-[#0f172a] shadow-[0_20px_40px_rgba(2,6,23,0.45)] ring-1 ring-slate-700/70'
    : 'overflow-hidden rounded-xl bg-white shadow-2xl ring-1 ring-black/10';
  const headerClassName = isDarkTheme
    ? 'flex items-center justify-between border-b border-slate-700/60 bg-slate-800/90 px-4 py-3 text-slate-100 backdrop-blur-sm'
    : 'flex items-center justify-between border-b border-blue-100 bg-gradient-to-r from-white via-slate-50 to-blue-50 px-4 py-3 text-slate-700';
  const launcherBadgeClassName = isDarkTheme
    ? 'rounded-full bg-blue-500/15 px-2 py-0.5 text-xs font-semibold tracking-wide text-blue-100'
    : 'rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold tracking-wide text-blue-700';
  const headerBadgeClassName = isDarkTheme
    ? 'rounded-full bg-blue-500/15 px-2 py-0.5 text-[11px] font-semibold tracking-wide text-blue-100'
    : 'rounded-full bg-blue-100 px-2 py-0.5 text-[11px] font-semibold tracking-wide text-blue-700';
  const headerLabelClassName = isDarkTheme
    ? 'truncate text-xs text-slate-200'
    : 'truncate text-xs text-slate-500';
  const headerActionClassName = isDarkTheme
    ? 'rounded p-1 text-slate-300 hover:bg-slate-700/80 hover:text-blue-100'
    : 'rounded p-1 text-slate-500 hover:bg-blue-100 hover:text-blue-700';

  if (!isAuthenticated) return null;
  if (!isOpen) {
    return (
      <button
        className={launcherClassName}
        onClick={openChat}
        aria-label={t('chat_open', { name: assistantName, defaultValue: 'Open {{name}}' })}
        data-theme-state={currentTheme}
      >
        <img
          src="/logo-favicon.png"
          alt="SyncLyst"
          className="h-7 w-7 object-contain"
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            target.style.display = 'none';
            const fallback = target.nextElementSibling as HTMLElement;
            if (fallback) fallback.style.display = 'flex';
          }}
        />
        <div
          className={`hidden h-7 w-7 items-center justify-center rounded-full text-xs font-bold ${isDarkTheme ? 'bg-blue-500/15 text-blue-100' : 'bg-blue-100 text-blue-700'}`}
          style={{ display: 'none' }}
        >
          AI
        </div>
        <span className={launcherBadgeClassName}>
          AI
        </span>
      </button>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-[9999] w-[calc(100vw-2rem)] sm:w-[400px] lg:w-[440px] max-w-full">
      <div className={shellClassName} data-theme-state={currentTheme}>
        <div className={headerClassName}>
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <img
                src="/logo-sidebar.png"
                alt="SyncLyst"
                className="h-8 w-auto max-w-[110px] object-contain"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  const fallback = target.nextElementSibling as HTMLElement;
                  if (fallback) fallback.style.display = 'flex';
                }}
              />
              <div
                className={`hidden h-8 w-8 items-center justify-center rounded-lg ${isDarkTheme ? 'bg-blue-500/15 text-blue-100' : 'bg-blue-100 text-blue-700'}`}
                style={{ display: 'none' }}
              >
                <Bot size={16} />
              </div>
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className={headerBadgeClassName}>
                  AI
                </span>
                <div className={headerLabelClassName}>{t('ai_assistant_label')}</div>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              className={headerActionClassName}
              aria-label={
                isMinimized
                  ? t('chat_maximize', 'Maximize')
                  : t('chat_minimize', 'Minimize')
              }
              onClick={() => setIsMinimized((v) => !v)}
            >
              {isMinimized ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </button>
            <button
              className={headerActionClassName}
              aria-label={t('chat_close', 'Close')}
              onClick={closeChat}
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {!isMinimized && (
          <>
            <div
              ref={listRef}
              className="h-[260px] max-h-[50vh] space-y-3 overflow-y-auto px-4 pt-3 sm:h-[340px] dark:bg-slate-900"
            >
              {messages.map((m) => (
                <div
                  key={m.id}
                  className={
                    m.role === 'user'
                      ? 'flex justify-end'
                      : 'flex justify-start'
                  }
                >
                  <div
                    className={
                      m.role === 'user'
                        ? 'max-w-[85%] rounded-lg bg-blue-600 text-white px-3 py-2 text-sm break-words whitespace-pre-wrap'
                        : 'max-w-[85%] rounded-lg bg-gray-100 text-gray-800 px-3 py-2 text-sm break-words whitespace-pre-wrap dark:bg-slate-700 dark:text-slate-100'
                    }
                  >
                    {m.role === 'assistant' ? (
                      <ReactMarkdown>{m.content}</ReactMarkdown>
                    ) : (
                      m.content
                    )}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="text-xs text-gray-500 dark:text-slate-400">
                  {t('chat_responding', { name: assistantName, defaultValue: '{{name}} is responding…' })}
                </div>
              )}
            </div>

            <div className="border-t border-gray-200 p-3 dark:border-slate-700 dark:bg-slate-900">
              <div className="flex items-center gap-2">
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder={t('chat_input_placeholder', 'Type a message...')}
                  disabled={loading}
                  className="flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:placeholder:text-slate-400 disabled:opacity-60 disabled:cursor-not-allowed"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                />
                <button
                  onClick={() => handleSend()}
                  className="rounded-lg bg-blue-600 text-white p-2 hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={loading || !input.trim()}
                  aria-label={t('chat_send', 'Send')}
                >
                  <Send size={18} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default AIChatWidget;
